import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, FileText, HelpCircle, Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { pathForSlug } from "@/lib/site";

const EMPTY = { pages: [], faq: [], articles: [] };
const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

function Hl({ text, q }) {
  const toks = norm(q).split(/[^a-z0-9]+/).filter((t) => t.length >= 2);
  if (!text || !toks.length) return text || null;
  const n = norm(text);
  const marks = [];
  toks.forEach((t) => {
    let i = n.indexOf(t);
    while (i >= 0) { marks.push([i, i + t.length]); i = n.indexOf(t, i + t.length); }
  });
  if (!marks.length) return text;
  marks.sort((a, b) => a[0] - b[0]);
  const out = [];
  let pos = 0;
  marks.forEach(([s, e], k) => {
    if (s < pos) return;
    out.push(<Fragment key={`t${k}`}>{text.slice(pos, s)}</Fragment>, <mark key={`m${k}`} className="bg-transparent text-mr-accent font-semibold">{text.slice(s, e)}</mark>);
    pos = e;
  });
  out.push(<Fragment key="end">{text.slice(pos)}</Fragment>);
  return out;
}

export function SiteSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(EMPTY);
  const [active, setActive] = useState(0);
  const nav = useNavigate();
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(EMPTY); return undefined; }
    const t = setTimeout(() => { api.get("/public/search", { params: { q } }).then((r) => { setRes(r.data); setActive(0); }).catch(() => setRes(EMPTY)); }, 150);
    return () => clearTimeout(t);
  }, [q]);
  const groups = useMemo(() => [
    { label: "Pages", icon: FileText, items: res.pages.map((p) => ({ key: `page-${p.slug.replace(/\//g, "-")}`, title: p.title, text: p.snippet, to: pathForSlug(p.slug) })) },
    { label: "Articles", icon: BookOpen, items: res.articles.map((a) => ({ key: `article-${a.slug}`, title: a.title, text: a.excerpt, to: `/ressources/blog/${a.slug}` })) },
    { label: "Réponses de la FAQ", icon: HelpCircle, items: res.faq.map((f, k) => ({ key: `faq-${k}`, title: f.question, text: f.answer, to: pathForSlug(f.slug) })) },
  ], [res]);
  const flat = groups.flatMap((g) => g.items);
  const pick = (to) => { setOpen(false); setQ(""); nav(to); };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter" && flat[active]) { e.preventDefault(); pick(flat[active].to); }
  };
  const none = q.trim().length >= 2 && !flat.length;
  let idx = -1;
  return (
    <>
      <button onClick={() => setOpen(true)} aria-label="Rechercher" data-testid="header-search-button" className="grid place-items-center w-10 h-10 border border-mr-border rounded-[3px] hover:border-mr-accent transition-colors"><Search size={16} /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="p-0 gap-0 bg-mr-bg border-mr-border max-w-xl top-[12%] translate-y-0" data-testid="search-dialog">
          <DialogTitle className="sr-only">Rechercher sur le site</DialogTitle>
          <div className="flex items-center gap-3 px-4 border-b border-mr-border">
            <Search size={18} className="text-mr-muted" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown} placeholder="Rechercher une page, un article, une question…" data-testid="search-input" className="h-14 w-full bg-transparent outline-none text-[15px]" />
          </div>
          <div className="max-h-[60vh] overflow-y-auto" data-testid="search-results">
            {q.trim().length < 2 && <p className="px-4 py-6 text-sm text-mr-muted">Tapez au moins deux lettres. Astuce : Ctrl + K ou ⌘ + K ouvre la recherche.</p>}
            {none && (
              <div className="px-4 py-6 text-sm" data-testid="search-empty">
                <p className="text-mr-text2">Aucun résultat pour « {q} ».</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Link to="/ressources/faq" onClick={() => setOpen(false)} data-testid="search-empty-faq-link" className="mr-btn-secondary">Consulter la FAQ</Link>
                  <Link to="/demande-invitation" onClick={() => setOpen(false)} data-testid="search-empty-invitation-link" className="mr-btn-primary">Demander une invitation</Link>
                </div>
              </div>
            )}
            {groups.map(({ label, icon: I, items }) => items.length > 0 && (
              <div key={label} className="py-2">
                <p className="px-4 py-1 text-[11px] uppercase tracking-wider text-mr-muted">{label}</p>
                {items.map((it) => {
                  idx += 1;
                  const me = idx;
                  return (
                    <button key={it.key} onClick={() => pick(it.to)} onMouseEnter={() => setActive(me)} data-testid={`search-result-${it.key}`} aria-selected={active === me}
                      className={`w-full text-left flex gap-3 px-4 py-2.5 transition-colors ${active === me ? "bg-mr-surface" : ""}`}>
                      <span className="text-mr-accent mt-0.5"><I size={16} /></span>
                      <span className="min-w-0"><span className="block text-sm font-medium"><Hl text={it.title} q={q} /></span>
                        {it.text && <span className="block text-xs text-mr-muted mt-0.5 line-clamp-2"><Hl text={it.text} q={q} /></span>}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
